
import React from "react";
import { motion } from "framer-motion";
import {
  Scroll,
  Users,
  Briefcase,
  DollarSign,
  CheckSquare,
  ThumbsDown,
  Zap,
  AlertCircle,
  FileText,
} from "lucide-react";

/* ---------- Reusable Term Card ---------- */
const TermItem = ({ icon: Icon, title, color, children }) => (
  <motion.section
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.35 }}
    className="bg-slate-50 p-5 rounded-2xl border border-slate-100"
  >
    <h2 className="flex items-center text-sm font-bold text-slate-800 mb-3 gap-2">
      <Icon className={`w-5 h-5 ${color} shrink-0`} />
      {title}
    </h2>
    <div className="text-xs text-slate-600 leading-relaxed space-y-2">
      {children}
    </div>
  </motion.section>
);

/* ---------- Main Component ---------- */
const TermSection = () => {
  return (
    <div className="min-h-screen bg-[#f8fafc] font-outfit relative pb-20">

      {/* Dark Hero Background for Navbar Visibility */}
      <div className="absolute top-0 left-0 w-full h-[180px] bg-[#020617] z-0 overflow-hidden rounded-b-[2rem] shadow-xl">
        <div className="absolute inset-0 opacity-20">
          <img src="https://images.unsplash.com/photo-1497215728101-856f4ea42174?auto=format&fit=crop&q=80&w=2000" alt="cover" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#020617]/50 to-[#020617]"></div>
      </div>

      {/* Container */}
      <div className="relative z-10 pt-24 max-w-7xl w-full mx-auto px-4 md:px-6">

        {/* Breadcrumbs */}
        <div className="text-white text-xs font-bold uppercase tracking-widest mb-6 opacity-80 flex items-center gap-2">
          <a href="/home" className="hover:text-blue-300 transition-colors">Home</a>
          <span>/</span>
          <span>Terms of Service</span>
        </div>

        {/* White Canvas */}
        <div className="bg-white rounded-[2rem] shadow-xl border border-slate-100 p-6 md:p-10 overflow-hidden">

          {/* Compact Header */}
          <div className="flex items-start gap-4 mb-8 pb-6 border-b border-slate-100">
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
              <Scroll className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-800">Terms of Service</h2>
              <p className="text-xs text-slate-500 mt-0.5">EasyMarg — Please read these terms carefully before using our platform.</p>
            </div>
          </div>

          {/* Terms Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

            <TermItem icon={FileText} title="1. Platform Nature" color="text-blue-600">
              <p>EasyMarg is a <strong>technology platform and aggregator only</strong>. We are not a law firm, accounting firm, consulting firm, or advisory entity.</p>
              <ul className="list-disc list-inside space-y-1.5">
                <li>Offer legal, accounting, tax, financial, or treasury advice</li>
                <li>Guarantee outcomes or results</li>
                <li>Supervise or control professionals' work</li>
              </ul>
            </TermItem>

            <TermItem icon={Users} title="2. User Eligibility" color="text-indigo-600">
              <ul className="list-disc list-inside space-y-1.5">
                <li>You are at least 18 years old</li>
                <li>You are capable of entering a legally binding agreement</li>
                <li>Information provided by you is accurate and lawful</li>
              </ul>
            </TermItem>

            <TermItem icon={Briefcase} title="3. Professional Listings" color="text-green-600">
              <ul className="list-disc list-inside space-y-1.5">
                <li>Independent practitioners or firms</li>
                <li>Solely responsible for their services</li>
                <li>Must comply with applicable laws and ethical codes</li>
              </ul>
              <p>EasyMarg may verify basic credentials but does <strong>not certify professional competence or suitability</strong>.</p>
            </TermItem>

            <TermItem icon={DollarSign} title="4. Engagement & Fees" color="text-yellow-600">
              <ul className="list-disc list-inside space-y-1.5">
                <li>Engagements, timelines, and deliverables are strictly between users and professionals</li>
                <li>EasyMarg may collect platform or facilitation fees</li>
                <li>Professional fees are decided independently</li>
              </ul>
            </TermItem>

            <TermItem icon={CheckSquare} title="5. No Guarantee or Endorsement" color="text-teal-600">
              <p>Listing on EasyMarg does not constitute endorsement.</p>
              <p>Users must conduct their own due diligence before engagement.</p>
            </TermItem>

            <TermItem icon={ThumbsDown} title="6. Limitation of Liability" color="text-red-600">
              <ul className="list-disc list-inside space-y-1.5">
                <li>Professional advice or errors</li>
                <li>Delays or non-performance</li>
                <li>Financial, legal, or reputational losses</li>
                <li>Disputes between users and professionals</li>
              </ul>
            </TermItem>

            <TermItem icon={Zap} title="7. Prohibited Use" color="text-orange-600">
              <ul className="list-disc list-inside space-y-1.5">
                <li>Use for unlawful purposes</li>
                <li>Misrepresentation or impersonation</li>
                <li>Bypassing platform safeguards</li>
                <li>Harassment, coercion, or defamation</li>
              </ul>
            </TermItem>

            <TermItem icon={AlertCircle} title="8. Termination" color="text-purple-600">
              <ul className="list-disc list-inside space-y-1.5">
                <li>Violation of terms</li>
                <li>Misuse or unethical conduct</li>
                <li>Legal or regulatory requirements</li>
              </ul>
            </TermItem>

          </div>

        </div>
      </div>
    </div>
  );
};

export default TermSection;
